import * as React from 'react';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription } from './ui/drawer';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { ImgWithFallback } from './ui/img-with-fallback';
import { useMediaQuery } from '@hooks/useMediaQuery';
import {
  SocialPlatform,
  shareToSocialPlatform,
  getShareSheetUI,
  ShareScenario,
  ShareEntityType,
  type ShareContext,
} from '@utils/shareHelpers';
import { usePrefetchShareUrls } from '@hooks/usePrefetchShareUrls';
import { getEntityId } from '@lib/shortUrlCache';
import { getErrorName } from '@utils/sanitizeError';
import { buildSourceTaggedUrl } from '@lib/sourceTaggedUrl';
import { Link2Icon, MailIcon, MoreHorizontalIcon, QrCode } from 'lucide-react';
import { cn } from '@lib/utils';
import { useAmplitudeTracking } from '@hooks/useAmplitudeTracking';
import { ANALYTICS_EVENTS, type QrSurface } from '@lib/analytics-events';
import { useShareSheetStore } from '@stores/shareSheetStore';
import { ShareQrErrorBoundary } from '@components/share/ShareQrErrorBoundary';
import {
  WhatsAppIcon,
  TelegramIcon,
  LinkedInIcon,
  FacebookIcon,
  TwitterIcon,
} from './ui/social-icons';

// Lazy on purpose. GlobalShareSheet mounts this sheet at the app root (src/index.tsx), so a
// static import of the QR panel drags its whole chain — useQrSvg -> qrcode + dompurify,
// @utils/qrExport's canvas compositing, InviteQrCard — into the ENTRY chunk, downloaded by
// every visitor whether or not they ever open a QR. Mounted only once the QR action
// has actually been tapped, so the chunk is fetched at that moment and never before.
const ShareQrPanel = React.lazy(() =>
  import('@components/share/ShareQrPanel').then(m => ({ default: m.ShareQrPanel })),
);

// =============================================================================
// SOCIAL BUTTON CONFIGURATION
// =============================================================================

interface SocialButton {
  platform: SocialPlatform;
  label: string;
  icon: React.ReactNode;
  bgColor: string;
}

const socialButtons: SocialButton[] = [
  {
    platform: SocialPlatform.WhatsApp,
    label: 'WhatsApp',
    icon: <WhatsAppIcon />,
    bgColor: 'bg-[#25D366]',
  },
  {
    platform: SocialPlatform.Telegram,
    label: 'Telegram',
    icon: <TelegramIcon />,
    bgColor: 'bg-[#0088cc]',
  },
  {
    platform: SocialPlatform.Facebook,
    label: 'Facebook',
    icon: <FacebookIcon />,
    bgColor: 'bg-[#1877F2]',
  },
  {
    platform: SocialPlatform.LinkedIn,
    label: 'LinkedIn',
    icon: <LinkedInIcon />,
    bgColor: 'bg-[#0A66C2]',
  },
  {
    platform: SocialPlatform.Twitter,
    label: 'X',
    icon: <TwitterIcon />,
    bgColor: 'bg-black',
  },
  {
    platform: SocialPlatform.Email,
    label: 'Email',
    icon: <MailIcon className="w-6 h-6" />,
    bgColor: 'bg-gray-500',
  },
];

// =============================================================================
// COMPONENT PROPS
// =============================================================================

export interface SocialShareSheetProps {
  /** Whether the sheet is open */
  open: boolean;
  /** Callback when open state changes */
  onOpenChange: (open: boolean) => void;
  /** The share context containing all information about what's being shared */
  context: ShareContext;
  /** Optional callback when user shares to a platform (for analytics) */
  onShare?: (platform: SocialPlatform) => void;
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * SocialShareSheet - Responsive share sheet component
 *
 * Shows a bottom drawer on mobile and a centered dialog on desktop.
 * Provides buttons for sharing to various social platforms.
 *
 * The UI text (title, description) is automatically determined based on
 * the share context, including:
 * - Entity type (question, event, profile)
 * - Scenario (just posted, own content, others' content)
 * - Special configurations (e.g., Christmas event)
 */
export const SocialShareSheet: React.FC<SocialShareSheetProps> = ({
  open,
  onOpenChange,
  context,
  onShare,
}) => {
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const hasNativeShare = typeof navigator !== 'undefined' && !!navigator.share;
  const { trackEvent, trackMeaningfulAction } = useAmplitudeTracking();
  // The QR takes the entity image's place in the header rather than opening a second
  // drawer: stacking modal vaul roots makes them fight over scroll-lock/background
  // scale, and swapping in place costs one boolean instead of an open flag, a mount
  // latch and a visibility derivation. It also keeps the social buttons usable while
  // the QR is showing. Nothing renders under the Suspense boundary — so nothing
  // fetches the lazy chunk — until this flips true.
  const [showQr, setShowQr] = React.useState(false);

  // The "QR code" action, offered for events and Spaces alike and to anyone who can
  // share them — not just the organizer. It reaches the same page the sheet's own Copy
  // row already hands out, so it grants no access the sharer didn't have.
  //
  // The link is derived from `context.path`, like every other channel in this sheet
  // (see usePrefetchShareUrls). That matters for unlisted Spaces: `path` already carries
  // the `?invite=` token (useShareSheet), and rebuilding the URL from the slug instead
  // would drop it and send every scanner into the 403 gate. The `qr_*` source prefix is
  // what marks a scan (see src/lib/spaces/inviteLink.ts).
  //
  // Assembled by `buildSourceTaggedUrl`, which `buildSpaceInviteLink` also calls, so the
  // organizer card and this action emit ONE byte-identical URL for a Space. They used to
  // differ in query-param order, which the QR cache (`qrSvgKey`) keys on: two cache
  // entries and two visibly different QR images for one page.
  //
  // Unlike the other channels this link deliberately carries ONLY `source`: it skips
  // `getShareUrl`, so it has no `ref`/`ref_user`/`ref_event` and is not shortened
  // through go.meetball.fun (a short URL's TTL is set when it is made and a person's scan never
  // restarts it, so a printed QR's short URL would die 90 days after it was made however often
  // people scan it, TECH-698). A scan is therefore attributable to the
  // channel but not to the sharer — same as the organizer QR (buildSpaceInviteLink).
  //
  // Returning null for the other entity types is also what narrows the ShareContext
  // union — the entity-specific reads below happen inside the guard.
  const qrTarget = React.useMemo(() => {
    if (
      context.entityType !== ShareEntityType.Event &&
      context.entityType !== ShareEntityType.Community
    ) {
      return null;
    }
    const isEvent = context.entityType === ShareEntityType.Event;
    // Returned alongside the link, not recomputed by the analytics call sites: the
    // `qr_source` reported must be the `source=` the QR actually encodes, and one
    // ternary in one place is the only way that stays true.
    const qrSource = isEvent ? 'qr_event' : 'qr_invite';
    return {
      qrSource,
      link: buildSourceTaggedUrl(context.path, qrSource),
      downloadSlug: isEvent
        ? (context.eventSlug ?? context.eventId)
        : (context.communitySlug ?? context.communityId),
      // The QR is an inline graphic with no text of its own — the generated SVG carries no
      // <title>. Without this the swap is silent to a screen reader: the labelled entity
      // image simply disappears.
      label: `Scan to open ${context.title}`,
    };
  }, [context]);
  const showQrAction = qrTarget !== null;

  // openShareSheet can swap `context` while this component stays mounted
  // (`open` never goes false). A stale showQr=true would then carry the previous
  // entity's QR — or an empty header, for a context with no QR at all — into the
  // new one. Guarded render-phase reset (the InviteBanner shape) keys off
  // the same `getEntityId` shareAnalyticsBase below derives entity_id from — one
  // exhaustive switch rather than a parallel chain that can silently drift — prefixed
  // with entityType so an id collision across entity types (e.g. an event and a
  // community sharing a UUID) can never mask a real context swap.
  // `trackedQrShownKeyRef` below stores this same string, so the QR's "shown" latch
  // re-arms on a swap without needing a second reset here.
  const contextEntityKey = `${context.entityType}:${getEntityId(context)}`;
  const [qrContextKey, setQrContextKey] = React.useState(contextEntityKey);
  if (qrContextKey !== contextEntityKey) {
    setQrContextKey(contextEntityKey);
    setShowQr(false);
  }
  const secondaryActionCount = 1 + (showQrAction ? 1 : 0) + (hasNativeShare ? 1 : 0);

  // Pre-fetch all platform-specific short URLs when sheet opens
  // This eliminates async delay during click, solving Safari popup blocker issues
  const { getUrlForPlatform, isReady: urlsReady } = usePrefetchShareUrls(open ? context : null);

  const shareAnalyticsBase = React.useMemo(() => {
    const base: {
      entity_type: ShareEntityType;
      entity_id: string;
      entity_slug: string | null;
      scenario: ShareScenario;
      sharer_id: string;
      is_own_content: boolean;
    } = {
      entity_type: context.entityType,
      entity_id: getEntityId(context),
      entity_slug: null,
      scenario: context.scenario,
      sharer_id: context.sharerId,
      is_own_content: false,
    };

    if (context.entityType === ShareEntityType.Question) {
      const questionContext = context;
      base.entity_slug = questionContext.questionSlug ?? null;
      base.is_own_content = questionContext.isOwn;
    } else if (context.entityType === ShareEntityType.Event) {
      const eventContext = context;
      base.entity_slug = eventContext.eventSlug ?? null;
      base.is_own_content = eventContext.isOwn;
    } else if (context.entityType === ShareEntityType.Community) {
      const communityContext = context;
      base.entity_slug = communityContext.communitySlug ?? null;
      base.is_own_content = communityContext.isOwn;
    } else if (context.entityType === ShareEntityType.Profile) {
      const profileContext = context;
      base.entity_slug = profileContext.profileSlug ?? null;
      base.is_own_content = profileContext.isOwn;
    } else {
      // App invite - no per-entity id; getEntityId already resolved it to 'app'
      base.is_own_content = context.isOwn;
    }

    return base;
  }, [context]);

  /**
   * The QR channel's own events. QR fires NONE of the share_* family: it has no
   * platform, no recipient and no observable completion. `share_completed` in
   * particular is the K-factor numerator and the Sharers / Super Sharers cohort
   * definition, so a toggle that five taps fires five times would corrupt all three.
   * The honest pair is "shown here" and "scanned over there" (`qr_code_scanned`),
   * joined on `(qr_source, entity_slug)`.
   *
   * The link never enters the payload: a Space QR carries a live `?invite=` token and
   * urlSecretRedactionPlugin only redacts allowlisted properties.
   */
  const trackQrEvent = (eventName: string, extra?: { error_name: string | null }) => {
    if (!qrTarget) return;
    const surface: QrSurface = 'share_sheet';
    trackEvent(eventName, {
      surface,
      qr_source: qrTarget.qrSource,
      ...shareAnalyticsBase,
      ...extra,
    });
  };

  // One `qr_code_shown` per sheet opening per entity — share_initiated's latch below, plus
  // a re-arm on entity swap that share_initiated does not have (hence a key, not a boolean). The OFF→ON edge alone is not enough: on/off/on is three taps and one
  // share intent, and the QR is a cheap reversible toggle sitting next to the buttons a
  // hesitating sharer is already poking, so an unlatched edge lets fidgeting inflate the
  // show side of the QR funnel it is meant to measure.
  //
  // Holds the entity key rather than a bare boolean so a context swap re-arms itself: the
  // reset that clears `showQr` is a render-phase guard, and a ref must not be written
  // during render. Same entity-keyed shape as InviteQrCard's.
  const trackedQrShownKeyRef = React.useRef<string | null>(null);

  const hasTrackedOpenRef = React.useRef(false);
  React.useEffect(() => {
    if (open && !hasTrackedOpenRef.current) {
      hasTrackedOpenRef.current = true;
      trackEvent(ANALYTICS_EVENTS.share_initiated, {
        ...shareAnalyticsBase,
      });
    }

    if (!open) {
      hasTrackedOpenRef.current = false;
      trackedQrShownKeyRef.current = null;
      // A QR left on from the previous opening would come back with no OFF→ON edge to
      // report it — a QR on screen that no `qr_code_shown` accounts for. Reopening starts
      // on the entity image, exactly like a first open.
      setShowQr(false);
    }
  }, [open, shareAnalyticsBase, trackEvent]);

  // Get UI configuration based on context
  const uiConfig = React.useMemo(() => getShareSheetUI(context), [context]);

  // Determine header image: use entity image when provided, otherwise generic illustration
  const hasEntityImage = !!context.imageUrl;
  const isOnBehalfScenario = [
    ShareScenario.JustPostedOnBehalf,
    ShareScenario.OwnContentOnBehalf,
    ShareScenario.OthersContentOnBehalf,
  ].includes(context.scenario);
  const isClaimed = context.entityType === ShareEntityType.Question && context.isClaimed === true;
  const defaultIllustration =
    isOnBehalfScenario && !isClaimed ? '/share-illustration.svg' : '/share-general.svg';
  const headerImage = context.imageUrl ? encodeURI(context.imageUrl) : null;

  const headerTitle = uiConfig.subtitle
    ? uiConfig.title
    : hasEntityImage
      ? `Share ${context.title}`
      : uiConfig.title;

  const handleShare = async (platform: SocialPlatform) => {
    onShare?.(platform);

    trackEvent(ANALYTICS_EVENTS.share_platform_selected, {
      ...shareAnalyticsBase,
      platform,
    });

    // Get pre-fetched URL if available - this enables instant sharing without popup blocker issues
    const prefetchedUrl = urlsReady ? getUrlForPlatform(platform) : undefined;

    try {
      const completed = await shareToSocialPlatform(platform, context, prefetchedUrl);

      // A cancelled share (native OS sheet dismissed, or web share aborted) shared
      // nothing: don't fire completion analytics, don't latch the invite cap, and
      // keep the sheet open so the user can pick another platform or explicitly
      // dismiss. Latching here would permanently silence the post-help prompt for
      // a user who shared nothing. Only Native reports a real cancel today; every
      // other path returns true (see shareToSocialPlatform).
      if (!completed) {
        return;
      }

      // AIDEV-NOTE: Track share_recipient_identified for K-factor calculation
      // Determine share_method based on platform characteristics:
      // - Email: always 'direct' (1:1 communication)
      // - WhatsApp, Telegram: 'unknown' (could be direct or group)
      // - LinkedIn, Facebook, Twitter: 'broadcast' (public posts)
      // - Copy, Native: 'unknown' (can't detect usage)
      const getShareMethod = (p: SocialPlatform): 'direct' | 'broadcast' | 'unknown' => {
        switch (p) {
          case SocialPlatform.Email:
            return 'direct';
          case SocialPlatform.LinkedIn:
          case SocialPlatform.Facebook:
          case SocialPlatform.Twitter:
            return 'broadcast';
          default:
            return 'unknown';
        }
      };

      // AIDEV-NOTE: Removed estimated recipient_count (magic number was inaccurate).
      // Actual share reach is measured via source=app_share attribution when links are clicked.
      // K-factor = (shares * click-through-rate * signup-conversion-rate)
      // Real data: count users arriving with source=app_share in Amplitude

      trackEvent(ANALYTICS_EVENTS.share_recipient_identified, {
        platform,
        entity_type: context.entityType,
        entity_id: shareAnalyticsBase.entity_id,
        is_own_content: shareAnalyticsBase.is_own_content,
        share_method: getShareMethod(platform),
      });

      trackEvent(ANALYTICS_EVENTS.share_completed, {
        ...shareAnalyticsBase,
        platform,
      });
      trackMeaningfulAction('share_completed', {
        ...shareAnalyticsBase,
        platform,
      });

      // Flag the share BEFORE closing so the global close handler records this
      // as "shared", not "dismissed" (see GlobalShareSheet.handleOpenChange).
      useShareSheetStore.getState().markShared();

      onOpenChange(false);
    } catch (error) {
      trackEvent(ANALYTICS_EVENTS.share_failed, {
        ...shareAnalyticsBase,
        platform,
        error_name: getErrorName(error),
      });
    }
  };

  const content = (
    <div className="flex flex-col gap-5 pb-6 mt-2">
      {/* Social platform grid - all visible without scroll */}
      <div className="grid grid-cols-3 gap-4 px-4">
        {socialButtons.map(button => (
          <button
            key={button.platform}
            onClick={() => handleShare(button.platform)}
            className="flex flex-col items-center gap-2"
            aria-label={`Share on ${button.label}`}
          >
            <div
              className={`w-14 h-14 rounded-full ${button.bgColor} flex items-center justify-center text-white shadow-xs active:scale-95 transition-transform touch-manipulation`}
            >
              {button.icon}
            </div>
            <span className="text-xs text-gray-600 font-medium text-center">{button.label}</span>
          </button>
        ))}
      </div>

      {/* Secondary actions */}
      <div
        className={cn(
          'px-4',
          secondaryActionCount === 1
            ? 'flex justify-center'
            : secondaryActionCount === 2
              ? 'grid grid-cols-2 gap-3'
              : 'grid grid-cols-3 gap-3',
        )}
      >
        <Button
          variant="outline"
          className="h-12 gap-2 rounded-xl border-gray-200 active:bg-gray-100 touch-manipulation"
          onClick={() => handleShare(SocialPlatform.Copy)}
        >
          <Link2Icon className="w-5 h-5 text-gray-600" />
          <span className="text-gray-700">Copy link</span>
        </Button>

        {showQrAction && (
          <Button
            variant="outline"
            aria-pressed={showQr}
            className={cn(
              'h-12 gap-2 rounded-xl border-gray-200 active:bg-gray-100 touch-manipulation',
              showQr && 'border-mb-teal bg-mb-teal/10',
            )}
            onClick={() => {
              const next = !showQr;
              setShowQr(next);
              // OFF -> ON edge, once per opening. Hiding the QR is not a "shown", and
              // turning it back on is the same share intent — see trackedQrShownKeyRef.
              if (next && trackedQrShownKeyRef.current !== contextEntityKey) {
                trackedQrShownKeyRef.current = contextEntityKey;
                trackQrEvent(ANALYTICS_EVENTS.qr_code_shown);
              }
            }}
          >
            <QrCode className="w-5 h-5 text-gray-600" />
            <span className="text-gray-700">QR code</span>
          </Button>
        )}

        {hasNativeShare && (
          <Button
            variant="outline"
            className="h-12 gap-2 rounded-xl border-gray-200 active:bg-gray-100 touch-manipulation"
            onClick={() => handleShare(SocialPlatform.Native)}
          >
            <MoreHorizontalIcon className="w-5 h-5 text-gray-600" />
            <span className="text-gray-700">More</span>
          </Button>
        )}
      </div>
    </div>
  );

  // Header visual: the entity image, or — once the QR action is toggled on — the QR that
  // takes its place. Shared by both the desktop dialog and the mobile drawer below so the
  // two can't drift. The Suspense fallback mirrors the panel's FULL footprint (QR square
  // plus the Download button beneath it), not just the QR: this sits inline in an
  // already-visible header, so a shorter placeholder still shifts everything below it —
  // including the button the user's finger is on — when the chunk lands. A failed chunk
  // fetch is caught by the boundary around it rather than the app root's: React.lazy
  // rejections never reach installChunkErrorHandlers' window listeners, and the app-root
  // ErrorBoundary answers a chunk error with a full-screen reload — see ShareQrErrorBoundary.
  const headerVisual =
    showQr && qrTarget ? (
      <ShareQrErrorBoundary>
        <React.Suspense
          fallback={
            <div className="mb-2 flex flex-col items-center gap-2.5">
              <div className="aspect-square w-48 animate-pulse rounded-lg bg-gray-200" />
              <div className="h-11 w-36 animate-pulse rounded-full bg-gray-200" />
            </div>
          }
        >
          <ShareQrPanel
            link={qrTarget.link}
            downloadSlug={qrTarget.downloadSlug}
            label={qrTarget.label}
            onDownloaded={() => {
              trackQrEvent(ANALYTICS_EVENTS.qr_code_downloaded);
            }}
            onDownloadFailed={errorName => {
              trackQrEvent(ANALYTICS_EVENTS.qr_code_download_failed, { error_name: errorName });
            }}
          />
        </React.Suspense>
      </ShareQrErrorBoundary>
    ) : (
      <ImgWithFallback
        src={headerImage}
        alt={hasEntityImage ? context.title : 'Share illustration'}
        className={cn(
          'mt-0 mb-2 mx-auto rounded-xl',
          hasEntityImage
            ? 'w-40 h-40 object-cover'
            : isDesktop
              ? 'w-full max-w-80'
              : 'w-full max-w-52',
        )}
        loading="eager"
        fallback={() => (
          // Deliberately NOT the image's box. When the entity image is present the
          // image above is cropped to a 160px square, but the fallback is the wide
          // default illustration — squaring it would crop it to nonsense. So the
          // fallback always takes the no-entity-image width and lets its own aspect
          // ratio stand. `loading="eager"` matches the image it replaces: this is
          // above the fold in an already-open sheet, so there is nothing to defer.
          <img
            src={defaultIllustration}
            alt="Share illustration"
            className={cn(
              'mt-0 mb-2 mx-auto rounded-xl',
              isDesktop ? 'w-full max-w-80' : 'w-full max-w-52',
            )}
            loading="eager"
          />
        )}
      />
    );

  // The subtitle is identical in both branches; only Title/Description differ (Dialog*
  // vs Drawer* are different components), so keep the one shared bit shared.
  const headerSubtitle = uiConfig.subtitle && hasEntityImage && (
    <p className="text-sm text-muted-foreground text-center font-medium">{uiConfig.subtitle}</p>
  );

  // Both surfaces need a viewport-relative cap plus a scroll region. Neither
  // DialogContent nor DrawerContent bounds its own height (dialog.tsx is a centered
  // `grid` with no max-height; drawer.tsx is `bottom-0 h-auto`, where its `mt-24` is
  // inert), so before this the sheet simply grew past the viewport with nothing to
  // scroll. The QR header is ~86px taller than the entity image it replaces, which on a
  // small phone pushed the QR itself off the top — unreachable, and unscannable once its
  // finder patterns were clipped. `calc(100dvh-4rem)` matches the other tall drawers in
  // this repo (WeMetRosterDrawer, ShareToSpacesDrawer, CreateEventChoiceDrawer).
  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md max-h-[calc(100dvh-4rem)] overflow-y-auto">
          <DialogHeader>
            {headerVisual}
            {headerSubtitle}
            <DialogTitle className="text-center">{headerTitle}</DialogTitle>
            <DialogDescription className="line-clamp-3 text-center whitespace-pre-line">
              {uiConfig.description}
            </DialogDescription>
          </DialogHeader>
          {content}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      {/* The scroll region is an inner wrapper rather than DrawerContent itself so vaul's
          drag handle stays outside it and drag-to-dismiss keeps working. */}
      <DrawerContent className="bg-mb-offwhite [--mb-surface:var(--color-mb-offwhite)] max-h-[calc(100dvh-4rem)] overflow-hidden">
        <div className="min-h-0 flex-1 overflow-y-auto">
          <DrawerHeader>
            {headerVisual}
            {headerSubtitle}
            <DrawerTitle className="text-center">{headerTitle}</DrawerTitle>
            <DrawerDescription className="line-clamp-3 whitespace-pre-line">
              {uiConfig.description}
            </DrawerDescription>
          </DrawerHeader>
          {content}
        </div>
      </DrawerContent>
    </Drawer>
  );
};
